// Globals
import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Color } from 'antd/es/color-picker';

// Components
import { AiBotIcon, ChatbotStarIcon, CloseIcon, ResetIcon, SendIcon } from '@/icons';
import { Input } from '@/ui';
import { COLORS } from '@/theme';
import {
  StyledAiBadge,
  StyledDisclaimer,
  StyledHeaderActions,
  StyledHeaderAvatar,
  StyledHeaderIconButton,
  StyledHeaderInfo,
  StyledHeaderName,
  StyledHeaderStatus,
  StyledInputRow,
  StyledMessageAvatar,
  StyledMessageAvatarBadge,
  StyledMessageContent,
  StyledMessageHeader,
  StyledPreviewBody,
  StyledPreviewCard,
  StyledPreviewFooter,
  StyledPreviewHeader,
  StyledPreviewMessage,
  StyledSenderName,
  StyledSendButton,
  StyledStatusDot,
} from './AiBotPreviewStyles';

export type TAiBotPreviewProps = {
  color?: Color | string;
  senderName?: string;
  welcomeMessage?: string;
  disclaimerMessage?: string;
};

const AiBotPreview: FC<TAiBotPreviewProps> = ({ color, senderName, welcomeMessage, disclaimerMessage }) => {
  const { t } = useTranslation('common');
  const hex = (typeof color === 'string' ? color : color?.toHexString?.()) || COLORS.GRAY_6;
  const displayName = senderName || t('text_ai_bot_default_sender_name');

  return (
    <StyledPreviewCard>
      <StyledPreviewHeader $color={hex}>
        <StyledHeaderAvatar>
          <AiBotIcon />
        </StyledHeaderAvatar>
        <StyledHeaderInfo>
          <StyledHeaderName>{displayName}</StyledHeaderName>
          <StyledHeaderStatus>
            <StyledStatusDot />
            {t('text_ai_bot_status_online')}
          </StyledHeaderStatus>
        </StyledHeaderInfo>
        <StyledHeaderActions>
          <StyledHeaderIconButton aria-label={t('text_ai_bot_reset_action')} tabIndex={-1}>
            <ResetIcon />
          </StyledHeaderIconButton>
          <StyledHeaderIconButton aria-label={t('text_ai_bot_close_action')} tabIndex={-1}>
            <CloseIcon />
          </StyledHeaderIconButton>
        </StyledHeaderActions>
      </StyledPreviewHeader>
      <StyledPreviewBody>
        <StyledMessageAvatar $color={hex}>
          <AiBotIcon />
          <StyledMessageAvatarBadge $color={hex}>
            <ChatbotStarIcon />
          </StyledMessageAvatarBadge>
        </StyledMessageAvatar>
        <StyledMessageContent>
          <StyledMessageHeader>
            <StyledSenderName>{displayName}</StyledSenderName>
            <StyledAiBadge $color={hex}>
              <ChatbotStarIcon />
              {t('text_ai_bot_badge_ai')}
            </StyledAiBadge>
          </StyledMessageHeader>
          {welcomeMessage ? (
            <StyledPreviewMessage dangerouslySetInnerHTML={{ __html: welcomeMessage }} />
          ) : (
            <StyledPreviewMessage>{t('text_ai_bot_preview_message')}</StyledPreviewMessage>
          )}
        </StyledMessageContent>
      </StyledPreviewBody>
      <StyledPreviewFooter>
        <StyledInputRow>
          <Input
            readOnly
            variant="borderless"
            placeholder={t('text_ai_bot_message_placeholder', { senderName: displayName })}
          />
          <StyledSendButton $color={hex} aria-label={t('text_ai_bot_send_action')} tabIndex={-1}>
            <SendIcon />
          </StyledSendButton>
        </StyledInputRow>
        <StyledDisclaimer>
          {disclaimerMessage || t('text_ai_bot_preview_disclaimer', { senderName: displayName })}
        </StyledDisclaimer>
      </StyledPreviewFooter>
    </StyledPreviewCard>
  );
};

export default memo(AiBotPreview);
